import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { setSentryTenantContext, clearSentryTenantContext } from '@/lib/error-tracking';

export interface Organization {
  id: string;
  name: string;
  domain: string | null;
  status: string;
  settings: Record<string, any>;
  created_at: string;
  is_host_organization?: boolean;
  tenant_code?: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  setCurrentOrganization: (org: Organization) => void;
  userOrganizations: Organization[];
  isLoading: boolean;
  error: string | null;
  refreshOrganizations: () => Promise<void>;
  userRole: string | null;
  isAdmin: boolean;
}

// Exported so a parallel entry (e.g. the public storefront) can mount its own
// provider with a synthetic value resolved from the request host instead of an
// authenticated user session.
export const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const queryClient = useQueryClient();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>([]);
  const [userRoleMap, setUserRoleMap] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const syncOrganizationClaim = useCallback(async (organizationId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    const currentClaim =
      session?.user?.app_metadata?.organization_id ??
      session?.user?.user_metadata?.organization_id;

    if (!session?.user || currentClaim === organizationId) {
      return;
    }

    const { error: fnError } = await supabase.functions.invoke('set-organization', {
      body: { organization_id: organizationId },
    });

    if (fnError) {
      console.warn("Failed to sync active organization to auth metadata:", fnError);
      return;
    }

    await supabase.auth.refreshSession();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['sales-territories'] }),
      queryClient.invalidateQueries({ queryKey: ['customer-groups'] }),
      queryClient.invalidateQueries({ queryKey: ['sales-channels'] }),
      queryClient.invalidateQueries({ queryKey: ['payment-terms'] }),
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] }),
      queryClient.invalidateQueries({ queryKey: ['shipping-methods'] }),
    ]);
  }, [queryClient]);

  const fetchUserOrganizations = async () => {
    try {
      // Don't keep resetting status if we already have a session/data
      if (!currentOrganization) {
        setIsLoading(true);
      }
      setError(null);

      // Get the session first - much faster than getUser()
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Get the user's organizations
      const { data: userOrgs, error: userOrgsError } = await supabase
        .from('user_organizations')
        .select('organization_id, is_primary, role')
        .eq('user_id', user.id);
      
      if (userOrgsError) throw userOrgsError;

      if (!userOrgs || userOrgs.length === 0) {
        setIsLoading(false);
        return;
      }

      const validOrgIds = userOrgs
        .map(uo => uo.organization_id)
        .filter(id => id && id !== 'null');

      if (validOrgIds.length === 0) {
        setIsLoading(false);
        return;
      }

      // Get the organization details - fetching tenant_code directly here
      const { data: organizationsRaw, error: orgsError } = await supabase
        .from('organizations')
        .select('*, is_host_organization, tenant_code')
        .in('id', validOrgIds);

      if (orgsError) throw orgsError;

      // Hide archived/terminated/suspended/offboarded organizations from the
      // selector. The tenant-auth-security edge function blocks login when a
      // user has no active orgs at all, but if a user belongs to *both* an
      // active and an inactive org we still need to keep them out of the
      // inactive one. Treat anything other than 'active' as inactive.
      const organizations = ((organizationsRaw ?? []) as Organization[]).filter(
        (org) => (org.status ?? 'active') === 'active',
      );

      // Drop any cached currentOrganizationId that points at an org that has
      // since been archived; otherwise the next render would still try to use it.
      const storedOrgId = localStorage.getItem('currentOrganizationId');
      if (storedOrgId && !organizations.find((org) => org.id === storedOrgId)) {
        localStorage.removeItem('currentOrganizationId');
      }

      if (userOrgs && userOrgs.length > 0) {
        const roleMap: Record<string, string> = {};
        userOrgs.forEach(uo => {
          if (uo.organization_id && uo.role) {
            roleMap[uo.organization_id] = uo.role;
          }
        });
        setUserRoleMap(roleMap);

        setUserOrganizations(organizations);

        if (organizations.length === 0) {
          // No active organizations available. The user may already have an
          // active session from before the tenant was archived; sign them out
          // so the next page load lands on /login, where the precheck will
          // also reject the sign-in attempt.
          setError('Your organization is no longer active. Please contact your administrator or support.');
          setCurrentOrganization(null);
          try {
            await supabase.auth.signOut();
          } catch (signOutErr) {
            console.warn('Failed to sign out user with no active organizations:', signOutErr);
          }
        } else {
          const primaryOrgId = userOrgs.find(uo => uo.is_primary)?.organization_id;
          const primaryOrg = primaryOrgId
            ? organizations.find(org => org.id === primaryOrgId)
            : organizations[0];

          const storedOrg = (storedOrgId && storedOrgId !== 'null')
            ? organizations.find(org => org.id === storedOrgId)
            : null;

          const nextOrganization = (storedOrg || primaryOrg) as Organization | undefined;
          if (nextOrganization) {
            setCurrentOrganization(nextOrganization);
            void syncOrganizationClaim(nextOrganization.id);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserOrganizations();

    // Listen for auth state changes - fetch on initial sign in or sign out
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Only re-fetch if relevant event occurs
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        fetchUserOrganizations();
      }
      if (event === 'SIGNED_OUT') {
        clearSentryTenantContext();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Save the current organization to localStorage when it changes
  useEffect(() => {
    if (currentOrganization) {
      localStorage.setItem('currentOrganizationId', currentOrganization.id);
    } else {
      // Clean up invalid localStorage values
      const storedOrgId = localStorage.getItem('currentOrganizationId');
      if (storedOrgId === 'null' || storedOrgId === 'undefined' || !storedOrgId?.trim()) {
        localStorage.removeItem('currentOrganizationId');
      }
    }
  }, [currentOrganization]);

  const handleSetCurrentOrganization = useCallback((org: Organization) => {
    setCurrentOrganization(org);
    void syncOrganizationClaim(org.id);
  }, [syncOrganizationClaim]);

  const refreshOrganizations = useCallback(async () => {
    await fetchUserOrganizations();
  }, []);

  const userRole = currentOrganization ? userRoleMap[currentOrganization.id] || null : null;
  const isAdmin = userRole?.toLowerCase() === 'admin' || 
                  userRole?.toLowerCase() === 'administrator' || 
                  userRole?.toLowerCase() === 'owner' || 
                  userRole?.toLowerCase() === 'superuser';

  useEffect(() => {
    if (!currentOrganization) return;
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      const plan = (currentOrganization.settings?.plan as string | undefined) ?? null;
      setSentryTenantContext({
        userId: session?.user?.id ?? null,
        organizationId: currentOrganization.id,
        plan,
        role: userRole,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrganization, userRole]);

  const contextValue = useMemo(() => ({
    currentOrganization,
    setCurrentOrganization: handleSetCurrentOrganization,
    userOrganizations,
    isLoading,
    error,
    refreshOrganizations,
    userRole,
    isAdmin,
  }), [currentOrganization, handleSetCurrentOrganization, userOrganizations, isLoading, error, refreshOrganizations, userRole, isAdmin]);

  return (
    <OrganizationContext.Provider
      value={contextValue}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
